<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Training and Enablement — QikSolve',
  description:
    'Role-based training and enablement for Microsoft 365 governance, validation practice, and accountable quality operations.',
});

const page = {
  hero: {
    eyebrow: 'Training and enablement',
    title: 'Build the competency that keeps quality practice workable',
    summary:
      'Systems only work as well as the people who use, review, and sustain them. Training and enablement helps teams understand the role they play, the controls they need to follow, and the evidence that supports good quality practice.',
    cta: { label: 'Discuss your training needs', to: '/contact' },
  },
  intro: {
    title: 'Good practice depends on understanding and accountability',
    content:
      'A good governance model or quality system is only effective when people know how to operate it, review it, and adapt to change. Structured training helps turn policy, process, and control intent into repeatable practice across the team.',
    items: [
      {
        title: 'Role-based understanding',
        description: 'Different roles need different guidance: system owners, users, reviewers, approvers, and quality leaders each have different responsibilities.',
      },
      {
        title: 'Competency that is usable',
        description: 'Training should support day-to-day work, not simply confirm attendance or awareness in abstract terms.',
      },
      {
        title: 'Evidence of capability',
        description: 'When structured correctly, records of training and enablement can support the broader evidence model behind quality practice.',
      },
    ],
  },
  sections: [
    {
      title: 'What training and enablement supports',
      items: [
        {
          title: 'Governance and roles',
          description: 'Help teams understand organisational responsibilities, access boundaries, approval pathways, and accountability expectations.',
        },
        {
          title: 'Validation and review practice',
          description: 'Clarify how validation expectations, system use, and change control connect to the work people actually perform.',
        },
        {
          title: 'AI-aware operating discipline',
          description: 'Support users and reviewers in understanding the governance model for generative AI and agentic workflows where relevant.',
        },
        {
          title: 'Quality system familiarity',
          description: 'Build a practical understanding of quality records, responsibilities, reviews, and continual improvement in context.',
        },
      ],
    },
    {
      title: 'Why it matters in regulated settings',
      items: [
        {
          title: 'Reduces dependency on informal knowledge',
          description: 'When capability is concentrated in a few people, the organisation becomes more vulnerable to gaps, inconsistency, and confusion.',
        },
        {
          title: 'Supports evidence and assurance',
          description: 'Competency records and role clarity can strengthen the quality evidence base and make oversight more practical.',
        },
        {
          title: 'Improves adoption and consistency',
          description: 'A clearer understanding of roles and expectations helps change land more reliably across teams and processes.',
        },
      ],
    },
    {
      title: 'How QikSolve can help',
      items: [
        {
          title: 'Role-based design',
          description: 'We help map learning to the roles, responsibilities, and decisions that matter most in the operating model.',
        },
        {
          title: 'Practical enablement',
          description: 'We focus on building real understanding of how controls, process, and evidence connect in everyday work.',
        },
        {
          title: 'Sustainable quality capability',
          description: 'Training is framed as part of a wider operating model that supports quality practice over time, not just a one-off learning event.',
        },
      ],
    },
  ],
  callout: {
    title: 'Training supports control, but does not replace it',
    content:
      'People need clear roles, process understanding, and evidence habits to operate effectively. Training strengthens the system, but the organisation still owns the decisions, oversight, and accountability behind good quality practice.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Related practice areas',
    items: [
      {
        label: 'Regulatory compliance',
        description: 'How governance, records, and accountability become workable quality practice.',
        to: '/product/regulatory-compliance',
      },
      {
        label: 'SharePoint governance',
        description: 'How information controls, access, and role clarity support day-to-day quality operations.',
        to: '/product/sharepoint-governance',
      },
      {
        label: 'Agentic AI governance',
        description: 'A practical model for governing AI use where quality and accountability matter.',
        to: '/product/agentic-ai-governance',
      },
      {
        label: 'Microsoft 365 Copilot Tasks in regulated consulting',
        description: 'An existing article on task delegation, user oversight, and rollout considerations.',
        to: '/blog/unlocking-productivity-with-microsoft-365-copilot-tasks',
      },
      {
        label: 'QikSolve product pathways',
        description: 'Explore the broader set of pathways and service contexts behind the quality operating model.',
        to: '/product',
      },
    ],
  },
  cta: {
    title: 'Need to build capability in the right places?',
    content:
      'Bring the roles, risks, and quality decisions you are trying to support. We can help shape a practical enablement path.',
    label: 'Discuss your training needs',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
